/**
 * NMU-554: make `/files/...` assets resolve inside the CMS editor.
 *
 * Inline images are stored root-relative — `<img src="/files/embed/1/{token}.jpg">`
 * — and that route is implemented in **nmu-web only**. On the public site the
 * markup therefore resolves against an origin that serves it; in the admin the
 * browser asks the admin origin, whose nginx answers `/files/...` with the SPA's
 * own index.html. The editor gets `text/html` where it asked for an image, and
 * every inline picture renders as a broken placeholder — which is what the
 * ticket's screenshot shows, on an article whose images load perfectly for
 * readers.
 *
 * The rewrite is deliberately **symmetric and display-only**. SunEditor's value
 * *is* the stored document, so rewriting on the way in without rewriting on the
 * way out would persist `https://uat.nmuweb…/files/...` into the database — an
 * environment's hostname baked into content that is copied between environments,
 * which is the same class of problem as the one being fixed here and worse,
 * because it survives in the data.
 *
 * Only `src`/`href` values that begin `/files/` are touched, so nothing else in
 * the markup can be caught by it.
 */

/** Origin of the public site, which is the only thing that serves `/files/`. */
const publicOrigin = () => (process.env.REACT_APP_PREVIEW_ENDPOINT || "").replace(/\/+$/, "");

/** `src="/files/…"` or `href='/files/…'`, capturing the attribute and quote. */
const RELATIVE_FILES = /(\s(?:src|href)=)(["'])(\/files\/)/gi;

/**
 * Absolute → relative, for the same attributes. Built per call because the
 * origin is configuration, not a constant.
 */
const absoluteFiles = (origin) =>
  new RegExp(`(\\s(?:src|href)=)(["'])${origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(/files/)`, "gi");

/**
 * Rewrites stored markup so its assets load in the admin.
 *
 * Returns the input untouched when no public origin is configured: a build
 * without `REACT_APP_PREVIEW_ENDPOINT` still shows broken images, exactly as
 * before, rather than producing markup pointing at nowhere.
 */
export const toEditorHtml = (html) => {
  const origin = publicOrigin();
  if (typeof html !== "string" || !origin || !html.includes("/files/")) {
    return html;
  }
  return html.replace(RELATIVE_FILES, `$1$2${origin}$3`);
};

/**
 * Rewrites editor markup back to what belongs in the database.
 *
 * Runs whether or not an origin is configured — if the value ever did acquire
 * an absolute URL, this is what takes it back out, and doing that only when the
 * current build happens to be configured would make the repair conditional on
 * the very setting that caused it.
 */
export const toStoredHtml = (html) => {
  if (typeof html !== "string" || !html.includes("/files/")) {
    return html;
  }

  const origin = publicOrigin();
  const withoutOrigin = origin ? html.replace(absoluteFiles(origin), "$1$2$3") : html;

  /*
    Any other absolute origin pointing at /files/ is stripped too. Content is
    copied between environments, so a document saved on uat can arrive on
    production still carrying uat's hostname — this is the one place that sees
    it, and leaving it would serve production readers a uat URL.
  */
  return withoutOrigin.replace(/(\s(?:src|href)=)(["'])https?:\/\/[^"'/]+(\/files\/)/gi, "$1$2$3");
};
